import type { IpdBillPaymentInput, IpdBillPaymentMode, IpdDaycareLookupOption, PatientFormFields, ResponsiblePersonFormFields } from './types';
import { doctorOptionLabel } from '../../lib/doctor-name';
import { normalizePhoneInput } from '../../lib/phone-input';

/** Secondary payment slot options — Cash is always the fixed first slot, matching the OPD bill design. */
export const billSecondaryPaymentModes: IpdBillPaymentMode[] = ['UPI', 'Transfer to Bank Account', 'Cheque', 'Other', 'Online'];

export const freshPaymentRows = (): IpdBillPaymentInput[] => [
  { mode: 'Cash', bankName: null, amount: 0 },
  { mode: 'UPI', bankName: 'Axis Bank', amount: 0 },
];

export const formatIpdDateTime = (value: string) => {
  const parts = new Intl.DateTimeFormat('en-IN', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata',
  }).formatToParts(new Date(value));
  const valueFor = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? '';
  return `${valueFor('day')}-${valueFor('month')}-${valueFor('year')} ${valueFor('hour')}:${valueFor('minute')} ${valueFor('dayPeriod').toUpperCase()}`.trim();
};

export const currency = (value: number) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(value);

const amountWordsOnes = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const amountWordsTens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

const amountWordsTwoDigits = (value: number): string => {
  if (value < 20) return amountWordsOnes[value] ?? '';
  const tens = amountWordsTens[Math.floor(value / 10)] ?? '';
  const ones = value % 10 ? ` ${amountWordsOnes[value % 10] ?? ''}` : '';
  return `${tens}${ones}`;
};

const amountWordsThreeDigits = (value: number): string => {
  const hundreds = Math.floor(value / 100);
  const rest = value % 100;
  return [hundreds ? `${amountWordsOnes[hundreds]} Hundred` : '', rest ? amountWordsTwoDigits(rest) : ''].filter(Boolean).join(' ');
};

/** Indian-numbering (crore/lakh/thousand) amount-in-words, used on printed receipts. */
export const amountInWords = (amount: number): string => {
  const value = Math.floor(Math.abs(amount));
  if (value === 0) return 'Zero Rupees Only';
  const crore = Math.floor(value / 1_00_00_000);
  const lakh = Math.floor((value % 1_00_00_000) / 1_00_000);
  const thousand = Math.floor((value % 1_00_000) / 1000);
  const hundred = value % 1000;
  const parts = [
    crore ? `${amountWordsThreeDigits(crore)} Crore` : '',
    lakh ? `${amountWordsTwoDigits(lakh)} Lakh` : '',
    thousand ? `${amountWordsTwoDigits(thousand)} Thousand` : '',
    hundred ? amountWordsThreeDigits(hundred) : '',
  ].filter(Boolean);
  return `${parts.join(' ')} Rupees Only`;
};

export const todayInput = () => {
  const date = new Date();
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 10);
};

export const dateTimeInput = () => {
  const date = new Date();
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 16);
};

export const toDateTimeInput = (value: string) => {
  const date = new Date(value);
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 16);
};

export const optionList = (items: IpdDaycareLookupOption[] | undefined) => (items ?? []).map((item) => ({ value: item.id, label: doctorOptionLabel(item.label, item.code) }));

export const ageFromDateOfBirth = (dob: string): { ageYears: number; ageMonths: number; ageDays: number } => {
  const birth = new Date(dob);
  const now = new Date();
  let years = now.getFullYear() - birth.getFullYear();
  let months = now.getMonth() - birth.getMonth();
  let days = now.getDate() - birth.getDate();
  if (days < 0) { months -= 1; days += new Date(now.getFullYear(), now.getMonth(), 0).getDate(); }
  if (months < 0) { years -= 1; months += 12; }
  return { ageYears: Math.max(0, years), ageMonths: Math.max(0, months), ageDays: Math.max(0, days) };
};

export const blankPatientFields = (): PatientFormFields => ({
  patientId: null, uhid: null, name: '', phone: null, gender: 'Male',
  maritalStatus: null, guardianName: null, relation: null, alternativeNumber: null, dateOfBirth: null,
  ageYears: 0, ageMonths: 0, ageDays: 0,
  address: null, state: 'West Bengal (WB)', district: null, pinCode: null, aadhaar: null,
});

export const patientFieldsFromOption = (option: IpdDaycareLookupOption): PatientFormFields => {
  let ageYears = 0;
  if (option.dateOfBirth) {
    const birth = new Date(option.dateOfBirth);
    const now = new Date();
    ageYears = Math.max(0, now.getFullYear() - birth.getFullYear() - (now < new Date(now.getFullYear(), birth.getMonth(), birth.getDate()) ? 1 : 0));
  }
  return {
    patientId: option.id,
    uhid: option.code ?? null,
    name: option.label,
    phone: option.phone ? normalizePhoneInput(option.phone) : null,
    gender: option.gender === 'female' ? 'Female' : option.gender === 'male' ? 'Male' : 'Others',
    maritalStatus: null,
    guardianName: null,
    relation: null,
    alternativeNumber: null,
    dateOfBirth: option.dateOfBirth ?? null,
    ageYears,
    ageMonths: 0,
    ageDays: 0,
    address: option.address ?? null,
    state: option.state ?? 'West Bengal (WB)',
    district: option.district ?? null,
    pinCode: option.pinCode ?? null,
    aadhaar: null,
  };
};

export const blankResponsiblePerson = (): ResponsiblePersonFormFields => ({ name: '', relation: '', phone: '', age: null, address: '' });
